/**
 * Inventory Alerts Widget
 *
 * Displays critical inventory alerts including low stock,
 * out of stock, overstock, and expiring items.
 * Connected to live Dashboard API with mock fallback.
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

import React from 'react';
import {
  AlertTriangle,
  AlertCircle,
  PackageX,
  PackagePlus,
  Clock,
} from 'lucide-react';
import { WidgetInstance, InventoryAlert } from '../../types';
import { getInventoryAlerts } from '../../mockDataService';
import { fetchInventoryAlerts } from '../../../../../../services/dashboardService';
import { useWidgetData } from '../../hooks/useWidgetData';
import WidgetWrapper from '../WidgetWrapper';
import { WidgetSkeleton } from '../shared';

interface InventoryAlertsWidgetProps {
  instance: WidgetInstance;
}

const InventoryAlertsWidget: React.FC<InventoryAlertsWidgetProps> = ({ instance }) => {
  const { data, isLoading, error, refresh, isFirstLoad, isLive } = useWidgetData<InventoryAlert[]>({
    widgetId: 'inventory-alerts',
    instance,
    fetchFn: async () => {
      try {
        return await fetchInventoryAlerts();
      } catch {
        return getInventoryAlerts();
      }
    },
  });

  if (isFirstLoad) {
    return (
      <WidgetWrapper instance={instance} onRefresh={refresh} isLoading={true}>
        <WidgetSkeleton variant="list" count={4} />
      </WidgetWrapper>
    );
  }

  const alerts = data ?? [];

  const getAlertIcon = (type: InventoryAlert['alertType']) => {
    switch (type) {
      case 'out_of_stock':
        return <PackageX className="w-4 h-4" />;
      case 'low_stock':
        return <AlertTriangle className="w-4 h-4" />;
      case 'overstock':
        return <PackagePlus className="w-4 h-4" />;
      case 'expiring':
        return <Clock className="w-4 h-4" />;
    }
  };

  const getAlertStyle = (alert: InventoryAlert) => {
    if (alert.severity === 'critical') {
      return 'bg-red-500/10 border-red-500/30 text-red-400';
    }
    return 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400';
  };

  const getAlertLabel = (type: InventoryAlert['alertType']) => {
    switch (type) {
      case 'out_of_stock':
        return 'Utsolgt';
      case 'low_stock':
        return 'Lav beholdning';
      case 'overstock':
        return 'Overbeholdning';
      case 'expiring':
        return 'Utloper snart';
    }
  };

  const criticalCount = alerts.filter((a) => a.severity === 'critical').length;
  const warningCount = alerts.filter((a) => a.severity === 'warning').length;

  return (
    <WidgetWrapper instance={instance} onRefresh={refresh} isLoading={isLoading} error={error?.message} isLive={isLive}>
      <div className="h-full flex flex-col">
        {/* Summary bar */}
        <div className="flex items-center gap-4 mb-3 pb-3 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-sm text-gray-400">
              {criticalCount} kritiske
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="text-sm text-gray-400">
              {warningCount} advarsler
            </span>
          </div>
        </div>

        {/* Alerts list */}
        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {alerts.map((alert) => (
            <div
              key={alert.id}
              className={`
                p-2 rounded-lg border
                ${getAlertStyle(alert)}
              `}
            >
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 mt-0.5">
                  {getAlertIcon(alert.alertType)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-white truncate">
                      {alert.itemNo}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded bg-gray-700/50 flex-shrink-0">
                      {getAlertLabel(alert.alertType)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 truncate mt-0.5">
                    {alert.itemName}
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-xs">
                    <span>
                      Beholdning: <strong className="text-white">{alert.currentQty}</strong>
                    </span>
                    {alert.minQty !== undefined && (
                      <span className="text-gray-500">
                        Min: {alert.minQty}
                      </span>
                    )}
                    {alert.location && (
                      <span className="text-gray-500 font-mono">
                        {alert.location}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {alerts.length === 0 && !isLoading && (
            <div className="h-full flex flex-col items-center justify-center text-gray-500">
              <AlertCircle className="w-8 h-8 mb-2 text-green-500" />
              <span className="text-sm">Ingen aktive varsler</span>
              <span className="text-xs text-gray-600 mt-1">Alt ser bra ut!</span>
            </div>
          )}
        </div>
      </div>
    </WidgetWrapper>
  );
};

export default InventoryAlertsWidget;
